import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Menu, X, ArrowUpRight, Globe, Check } from "lucide-react";
import { useLocale } from "@/i18n/LocaleContext";
import { LANGS } from "@/i18n/translations";
import { smePath } from "@/i18n/sme";

export const Header = () => {
  const { t, locale } = useLocale();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!langOpen) return;
    const onClick = (e) => {
      if (!e.target.closest("[data-lang-switcher]")) setLangOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [langOpen]);

  const links = [
    { id: "solutions", label: t.nav.solutions, href: "#dual" },
    { id: "services", label: t.nav.services, href: "#services" },
    { id: "ventures", label: t.nav.ventures, href: "#ventures" },
    { id: "about", label: t.nav.about, href: "#about" },
    { id: "contact", label: t.nav.contact, href: "#contact" },
    { id: "small-business", label: t.nav.smallBusiness, to: smePath(locale), accent: true },
  ];

  const chooseLang = (l) => {
    localStorage.setItem("nowi_locale_chosen", "1");
    setLangOpen(false);
    setOpen(false);
    navigate(l.path);
    window.scrollTo({ top: 0 });
  };

  const current = LANGS.find((l) => l.code === locale) || LANGS[0];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <a href="#top" className="font-syncopate font-bold text-lg tracking-[0.15em] text-nowi-text" data-testid="logo-link">
          NOWI<span className="text-nowi-tech">.</span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) =>
            l.to ? (
              <Link
                key={l.id}
                to={l.to}
                data-testid={`nav-${l.id}-link`}
                className="group relative font-mono text-[11px] uppercase tracking-[0.18em] text-nowi-venture hover:text-nowi-text transition-colors whitespace-nowrap"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-nowi-venture group-hover:w-full transition-all duration-300" />
              </Link>
            ) : (
              <a
                key={l.id}
                href={l.href}
                data-testid={`nav-${l.id}-link`}
                className="group relative font-mono text-[11px] uppercase tracking-[0.18em] text-nowi-muted hover:text-nowi-text transition-colors whitespace-nowrap"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-nowi-text group-hover:w-full transition-all duration-300" />
              </a>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          {/* Language switcher */}
          <div className="relative" data-lang-switcher>
            <button
              onClick={() => setLangOpen(!langOpen)}
              data-testid="lang-switcher-btn"
              className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-nowi-muted hover:text-nowi-text transition-colors"
            >
              <Globe className="w-4 h-4" strokeWidth={1.5} />
              {current.label}
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="absolute right-0 mt-3 w-40 bg-black/90 backdrop-blur-xl border border-white/10"
                  data-testid="lang-menu"
                >
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => chooseLang(l)}
                      data-testid={`lang-option-${l.code}`}
                      className={`w-full flex items-center justify-between px-4 py-3 text-left font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                        l.code === locale ? "text-nowi-tech" : "text-nowi-muted hover:text-nowi-text hover:bg-white/5"
                      }`}
                    >
                      {l.full}
                      {l.code === locale && <Check className="w-3.5 h-3.5" strokeWidth={1.5} />}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <a
            href="#contact"
            data-testid="header-cta"
            className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 text-xs font-mono uppercase tracking-[0.15em] text-nowi-text hover:bg-nowi-text hover:text-nowi-bg transition-colors duration-300"
          >
            {t.nav.letsTalk} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </a>
        </div>

        <button
          className="lg:hidden text-nowi-text"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-black/90 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l) =>
                l.to ? (
                  <Link
                    key={l.id}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="font-playfair text-2xl text-nowi-venture"
                    data-testid={`mobile-nav-${l.id}-link`}
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.id}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="font-playfair text-2xl text-nowi-text"
                    data-testid={`mobile-nav-${l.id}-link`}
                  >
                    {l.label}
                  </a>
                )
              )}
              <div className="flex gap-3 pt-2">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => chooseLang(l)}
                    data-testid={`mobile-lang-option-${l.code}`}
                    className={`font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-2 border ${
                      l.code === locale ? "border-nowi-tech text-nowi-tech" : "border-white/15 text-nowi-muted"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-xs font-mono uppercase tracking-[0.15em]"
                data-testid="mobile-header-cta"
              >
                {t.nav.letsTalk} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
